import {
  Modal,
  Switch,
  CircularProgress
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useBookOrder } from 'store/books/selectors';
import { useCurrentOrgPermission } from 'store/organizations/selectors';
import {
  handleGetClassTogglePermission,
  handleUpdateClassTogglePermission
} from 'store/organizations/thunks';
import { useUserClasses } from 'store/userClasses/selectors';
import { AITopic, profileArrow } from 'utils/assets/images';
import CloseIconss from '../../utils/assets/images/closes.svg';

//  -- Features -----------------------
import * as Sentry from '@sentry/react';
import SentryWrap from 'components/Sentry';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { find, get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUserProfileConfig } from 'store/users/selectors';
import SpinLoader from 'components/Loader/SpinLoader';

const TeacherPermission = ({
  currentUser,
  currentClass,
  permissionsModalOpen,
  setPermissionsModalOpen,
}) => {
  const showAlert = useAlert();
  const userClasses = useUserClasses();
  const bookOrder = useBookOrder();
  const orgPermissions = useCurrentOrgPermission();

  // State for individual book group permissions
  const [bookGroupPermissions, setBookGroupPermissions] = useState({
    en_sp: {},
    vocabulary: {},
    cognates: {},
    sentence_starters: {},
  });
  const [toggleLoading, setToggleLoading] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const latestBookGroupPermissionsRef = useRef({
    en_sp: {},
    vocabulary: {},
    cognates: {},
    sentence_starters: {},
  });
  const dispatch = useDispatch();
  const [selectedClassId, setSelectedClassId] = useState('');
  const [aiPermissionLoading, setAiPermissionLoading] = useState(false);
  const { profile_config, updateserProfileConfig } = useUserProfileConfig()
  const isAiPermissionEnabled = useMemo(() => {
    const p_config = profile_config?.config || {};
    if (typeof p_config?.ai_enabled !== "boolean") return true;
    return p_config?.ai_enabled === true;
  }, [profile_config])
  const handleChange = (event) => {
    setSelectedClassId(event.target.value);
  };

  // Function to update book group permission
  const updateBookGroupPermission = (permissionType, bookKey, value) => {
    const newPermissions = {
      en_sp: { ...bookGroupPermissions.en_sp },
      vocabulary: { ...bookGroupPermissions.vocabulary },
      cognates: { ...bookGroupPermissions.cognates },
      sentence_starters: { ...bookGroupPermissions.sentence_starters },
      [permissionType]: {
        ...bookGroupPermissions[permissionType],
        [bookKey]: value,
      },
    };

    // Update state
    setBookGroupPermissions(newPermissions);

    // Update ref for immediate access
    latestBookGroupPermissionsRef.current = newPermissions;
  };

  // Debounced save function
  const debouncedSave = useCallback(async () => {
    try {
      setToggleLoading(true);
      // Use the ref to get the most current permissions data
      const data = {
        class_id: selectedClassId || currentClass.id,
        teacher_id: currentUser.id,
        en_sp: { ...latestBookGroupPermissionsRef.current.en_sp },
        vocabulary: { ...latestBookGroupPermissionsRef.current.vocabulary },
        cognates: { ...latestBookGroupPermissionsRef.current.cognates },
        sentence_starters: { ...latestBookGroupPermissionsRef.current.sentence_starters },
      };
      // Set pending save state
      setPendingSave(true);

      const response = await handleUpdateClassTogglePermission(data);
      setPendingSave(false);
      if (response && !response.error) {
      } else {
        console.error('Debounced save failed:', response);
        showAlert('Failed to save changes', 'error');
      }
    } catch (error) {
      console.error('Debounced save error:', error);
      showAlert('Failed to save changes', 'error');
    } finally {
      setToggleLoading(false);
    }
  }, [selectedClassId, currentClass.id, currentUser.id]);

  // Function to trigger debounced save
  const triggerDebouncedSave = useCallback(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedSave();
    }, 2000); // 2 second debounce
  }, [debouncedSave]);

  // Function to handle individual toggle updates with debounced save
  const handleToggleUpdate = (permissionType, bookKey, value) => {
    // Update local state immediately for responsive UI
    updateBookGroupPermission(permissionType, bookKey, value);

    // Trigger debounced save (will save after 2 seconds of no changes)
    triggerDebouncedSave();
  };

  const [nestedModal, setNestedModal] = useState(false);
  const [activePermissionType, setActivePermissionType] = useState('vocabulary'); // 'vocabulary', 'en_sp', 'cognates'
  const [aiConfirmModal, setAiConfirmModal] = useState(false);

  // Function to handle AI permission toggle
  const handleAIPermissionToggle = async (checked) => {
    try {
      setAiPermissionLoading(true);
      const response = await updateserProfileConfig({
        ai_enabled: checked
      });
      
      if (response && !response.error) {
        showAlert(`AI permission ${checked ? 'enabled' : 'disabled'} successfully`, 'success');
      } else {
        showAlert('Failed to update AI permission', 'error');
      }
    } catch (error) {
      console.error('AI permission toggle error:', error);
      showAlert('Failed to update AI permission', 'error');
    } finally {
      setAiPermissionLoading(false);
    }
  };

  // Function to handle AI toggle with confirmation for disable
  const handleAIToggleWithConfirmation = (checked) => {
    if (!checked) {
      setAiConfirmModal(true);
    } else {
      handleAIPermissionToggle(checked);
    }
  };

  // Function to confirm AI disable
  const confirmAIDisable = () => {
    setAiConfirmModal(false);
    handleAIPermissionToggle(false);
  };

  // Permission types configuration
  const permissionTypes = [
    {
      key: 'en_sp',
      label: 'Let students see the English translation per book page',
      modalTitle: 'Select sections/books where students should see English translation',
    },
    {
      key: 'vocabulary',
      label: 'Students can add or search vocabulary words',
      modalTitle: 'Select sections/books where students should see vocabulary words',
    },
    {
      key: 'cognates',
      label: 'Students can add or search cognates',
      modalTitle: 'Select sections/books where students should see cognates',
    },
    {
      key: 'sentence_starters',
      label: 'Students can access sentence starters',
      modalTitle: 'Select sections/books where students should see sentence starters',
    },
    {
      key: 'ai_permission',
      label: 'Enable / Disable AI features across the platform',
      icon:AITopic
    }
  ];

  // Get book groups for the selected class
  const getBookGroupsForSelectedClass = useMemo(() => {
    if (!selectedClassId || !userClasses || !bookOrder) return [];

    const selectedClass = userClasses.find((cls) => cls.id === selectedClassId);
    if (!selectedClass) return [];

    const books_group_permission = get(orgPermissions, 'books_group_permission', []);
    const archived_group = selectedClass.archived_group || [];
    const class_books_group = selectedClass.books_group || [];
    let order_group = [];
    // Additional book groups that should always be available
    const additionalBookGroups = [
      { key: 'balcónCultural', title: 'Balcón Cultural', color: 'rgb(27,45,79)' },
      { key: '1nformate', title: 'Infórmate', color: 'rgb(53,90,92)' },
      { key: 'conectandome', title: 'Conectándome', color: 'rgb(62,41,119)' },
      { key: 'comunidad', title: 'Comunidad', color: 'rgb(85,151,156)' },
      { key: 'ap', title: 'AP', color: 'rgb(237,187,80)' },
      { key: 'apExam', title: 'AP Exam', color: 'rgb(237,187,80)' },
      { key: 'standardExam', title: 'Standard Exam', color: 'rgb(62,41,119)' },
      { key: '1WAYSTORY', title: 'Story', color: 'rgb(53,90,92)' },
      { key: 'exams', title: 'Exams', color: 'rgb(62,41,119)' },
      { key: '1WAYRESOURCES', title: 'Resources', color: 'rgb(85,151,156)' }
    ];

    // Add book groups from bookOrder
    (bookOrder || []).forEach((order) => {
      if (archived_group.includes(order.key)) return;
      if (books_group_permission.length === 0 || books_group_permission.includes(order.key)) {
        const has_book = find(class_books_group, (k) => k === order.key);
        if (has_book) {
          order_group.push({
            key: order.key,
            title: order.title,
            color: order.color,
          });
        }
      }
    });

    // Add additional book groups
    additionalBookGroups.forEach((group) => {
      if (!archived_group.includes(group.key)) {
        // Always include AP Exam and Standard Exam regardless of org permission
        if (group.key === 'apExam' || group.key === 'standardExam') {
          order_group.push(group);
          return;
        }
        // Check if the book group is enabled in orgPermissions
        if (orgPermissions[group.key] === true) {
          order_group.push(group);
        }
      }
    });

    return order_group;
  }, [selectedClassId, userClasses, bookOrder, orgPermissions]);

  // Reset to current class when popup opens
  useEffect(() => {
    if (permissionsModalOpen && currentClass?.id) {
      setSelectedClassId(currentClass.id);
    }
  }, [permissionsModalOpen, currentClass]);

  useEffect(() => {
    // if (selectedClassId) {
      fetchData();
    // }
  }, [selectedClassId]);

  // Cleanup debounce timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    let classId = selectedClassId || currentClass.id;
    let teacherId = currentUser.id;
    if (!classId || !teacherId) return;

    try {


      // Fetch toggle permissions from the new API
      const classToggleData = await dispatch(
        handleGetClassTogglePermission(
          classId,
          currentUser?.role === 'student' ? currentClass?.teacherId : currentUser?.id,
        ),
      );
      const toggleData = classToggleData.data || {};
      if (toggleData && !toggleData.error) {
        // Update book group permissions with fetched data
        const newPermissions = {
          en_sp: toggleData.en_sp || {},
          vocabulary: toggleData.vocabulary || {},
          cognates: toggleData.cognates || {},
          sentence_starters: toggleData.sentence_starters || {},
        };

        setBookGroupPermissions(newPermissions);
        latestBookGroupPermissionsRef.current = newPermissions;
      } else {
        // Initialize with empty permissions if no data found
        const emptyPermissions = {
          en_sp: {},
          vocabulary: {},
          cognates: {},
          sentence_starters: {},
        };

        setBookGroupPermissions(emptyPermissions);
        latestBookGroupPermissionsRef.current = emptyPermissions;
      }
    } catch (error) {
      showAlert('Failed to fetch permission data', 'error');
      Sentry.captureException(`Error fetching class toggle permission: ${error}`);
    }
  };

  return (
    <SentryWrap>
    <SpinLoader dataloader={aiPermissionLoading} />
      <Modal
        open={permissionsModalOpen}
        onClose={() => setPermissionsModalOpen(false)}
        aria-labelledby="permissions-modal-title"
        aria-describedby="permissions-modal-description"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="permissionModal">
          <button className="permissionsMdlClose" onClick={() => setPermissionsModalOpen(false)}>
            <img src={CloseIconss} alt="Close" style={{ width: '20px', height: '20px' }} />
          </button>
          <div className="flex flex-row justify-between items-start">
            <div className="permissionsLabel">Permissions</div>
            <div className="permissionsLabel flex flex-row flexWrapOnMobileDevices items-center justify-center gap-6 margintop5" style={{ whiteSpace: 'nowrap' }}>
              <label style={{ color: '#ffffff !important', fontSize: '15px', whiteSpace: 'nowrap' }}>
                Change per class
              </label>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  color: '#ffffff !important',
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff !important',
                    height: 30,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiOutlinedInput-input': { padding: '0px 12px !important' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00E0FF', borderWidth: '2px' },
                  },
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 1.25,
                    minHeight: 'unset !important',
                    lineHeight: 'normal',
                    fontWeight: 500,
                  }, // compact padding
                  '& .MuiSelect-icon': { right: 8, color: '#ffffff !important' }, // tighter chevron
                }}
                style={{ marginLeft: 10 }}
              >
                <Select
                  value={selectedClassId}
                  displayEmpty
                  onChange={handleChange}
                  renderValue={(val) => {
                    if (!val) return 'Select Class';
                    const selectedClass = userClasses.find(
                      (cls) => cls.id === val && !cls.archived,
                    );
                    return selectedClass ? selectedClass.displayName : 'Select Class';
                  }}
                  inputProps={{ 'aria-label': 'Class' }}
                  IconComponent={() => <img src={profileArrow} alt="Select" style={{ width: '15px', cursor: 'pointer', marginRight: '8px' }} />}
                >
                  {userClasses && userClasses.length > 0 ? (
                    userClasses
                      .filter((cls) => !cls.archived)
                      .map((cls) => (
                        <MenuItem key={cls.id} value={cls.id}>
                          {cls.displayName}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem value="" disabled>
                      No classes available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="permissionModalText flex flex-col gap-20">
            {permissionTypes.map((permissionType) => (
              <div
                key={permissionType.key}
                className="flex flex-row justify-between items-center gap-30"
              >
                <div className="flex flex-row items-center gap-2">
                  {permissionType.icon && (
                    <img 
                      src={permissionType.icon} 
                      alt="AI" 
                      style={{ width: '20px', height: '20px' , marginRight: '8px' }} 
                    />
                  )}
                  <label>{permissionType.label}</label>
                </div>
                {permissionType.key === 'ai_permission' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {aiPermissionLoading && (
                      <CircularProgress 
                        size={16} 
                        style={{ color: '#00E0FF' }}
                      />
                    )}
                    <Switch
                      checked={isAiPermissionEnabled}
                      onChange={(e) => {
                        if (aiPermissionLoading) return;
                        handleAIToggleWithConfirmation(e.target.checked);
                      }}
                      disabled={aiPermissionLoading}
                      name="ai_permission"
                    />
                  </div>
                ) : (
                  <div
                    className="editButtonPermission"
                    onClick={() => {
                      setActivePermissionType(permissionType.key);
                      setNestedModal(true);
                    }}
                  >
                    <button className="editButton" style={{ cursor: 'pointer' }}>Edit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={nestedModal}
        onClose={() => setNestedModal(false)}
        aria-labelledby="permissions-modal-title"
        aria-describedby="permissions-modal-description"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '0 8px' }}
        >
          <div className="permissionModal">
            {/* <button className='permissionsMdlClose' onClick={() => setNestedModal(false)}>
                        <img src={CloseIconss} alt="Close" style={{ width: '20px', height: '20px' }} />
                    </button> */}
            <div className="flex flex-row justify-between items-center ">
              <div className="permissionsLabel">
                {permissionTypes.find((pt) => pt.key === activePermissionType)?.modalTitle ||
                  'Permission settings'}
              </div>
              <div className="flex flex-row items-center gap-2 marginBottom10" >
                {pendingSave && (
                  <div
                    style={{
                      color: '#ffa500',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#ffa500',
                        opacity: 0.7,
                      }}
                    ></div>
                    Saving...
                  </div>
                )}
                <div
                  className="permissionsLabelBack flex flex-col items-center justify-center gap-2"
                  onClick={() => setNestedModal(false)}
                >
                  Back
                </div>
              </div>
            </div>
             <div className="permissionModalText">
               <div className="permissionGrid">
                 {getBookGroupsForSelectedClass && getBookGroupsForSelectedClass.length > 0 ? (
                   getBookGroupsForSelectedClass.map((bookGroup, index) => (
                     <div
                       key={bookGroup.key}
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'space-between',
                         padding: '0px 0'
                       }}
                     >
                       <span
                         style={{
                           fontSize: '13px',
                           fontWeight: '500',
                           color: '#fff'
                         }}
                       >
                         {bookGroup.title}:
                       </span>
                       <Switch
                         checked={
                           activePermissionType === 'en_sp'
                             ? bookGroupPermissions[activePermissionType][bookGroup.key] === true
                             : bookGroupPermissions[activePermissionType][bookGroup.key] !== false
                         }
                         onChange={(e) => {
                           if (toggleLoading) return;
                           handleToggleUpdate(
                             activePermissionType,
                             bookGroup.key,
                             e.target.checked,
                           );
                         }}
                         name={activePermissionType}
                       />
                     </div>
                   ))
                 ) : (
                   <div 
                     style={{ 
                       gridColumn: '1 / -1',
                       textAlign: 'center', 
                       padding: '40px', 
                       color: '#666',
                       fontSize: '16px'
                     }}
                   >
                     No book groups available for the selected class
                   </div>
                 )}
               </div>
             </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={aiConfirmModal}
        onClose={() => setAiConfirmModal(false)}
        aria-labelledby="ai-confirm-modal-title"
        aria-describedby="ai-confirm-modal-description"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="permissionModal" style={{ 
          maxWidth: '500px', 
          width: '90%',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          border: '2px solid #ff4444',
          boxShadow: '0 8px 32px rgba(255, 68, 68, 0.3)'
        }}>
          <div className="permissionsLabel" style={{ marginBottom: '20px', textAlign: 'center' }}>
            Confirm AI Feature Disable
          </div>
          <div style={{ 
            color: '#ffffff', 
            fontSize: '16px', 
            lineHeight: '1.5', 
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            Disabling AI features will remove all AI-powered functionality from your account and all your students' accounts. You can enable these features again anytime from the settings. Are you sure you want to proceed?
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '20px',
            marginTop: '20px'
          }}>
            <button
              onClick={() => setAiConfirmModal(false)}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.6)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmAIDisable}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ff4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Disable AI Features
            </button>
          </div>
        </div>
      </Modal>
  </SentryWrap>
  );
};

export default TeacherPermission;
